import { DatePicker } from '@/shared/ui';
import { getDefaultEvalDate, isCurrentlyLate } from '@/shared/utils/date';
import {
    addMinutes,
    handleInputDateChange,
    MAX_CLASS_MIN,
    MAX_CLASS_PERIOD_MIN,
    timeAddArr,
} from '@/shared/utils/publish';
import moment from 'moment';
import { useEffect } from 'react';

export const PublishOptionPeriod = ({
    purpose,
    startDate,
    endDate,
    hours,
    minutes,
    periodWhether,
    timerWhether,
    setStartDate,
    setEndDate,
    setHours,
    setMinutes,
    setPeriodWhether,
    setTimerWhether,
}: {
    purpose?: DATA.PurposeType;
    startDate?: Date;
    endDate?: Date;
    hours: number;
    minutes: number;
    periodWhether: boolean;
    timerWhether: boolean;
    setStartDate: React.Dispatch<React.SetStateAction<Date | undefined>>;
    setEndDate: React.Dispatch<React.SetStateAction<Date | undefined>>;
    setHours: React.Dispatch<React.SetStateAction<number>>;
    setMinutes: React.Dispatch<React.SetStateAction<number>>;
    setPeriodWhether: React.Dispatch<React.SetStateAction<boolean>>;
    setTimerWhether: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
    useEffect(() => {
        if (purpose === 'class') {
            if (!periodWhether) {
                onDateChange(undefined, 's');
                onDateChange(undefined, 'e');
                setTimerWhether(true);
            }
            if (periodWhether) {
                const toDay = new Date();
                const toDayPlus7Days = new Date(toDay.getTime() + 7 * 24 * 60 * 60 * 1000);
                onDateChange(toDay, 's');
                onDateChange(toDayPlus7Days, 'e');
            }
        } else {
            if (!periodWhether) {
                const toDay = new Date();
                const toDayPlus7Days = new Date(toDay.getTime() + 7 * 24 * 60 * 60 * 1000);
                onDateChange(toDay, 's');
                onDateChange(toDayPlus7Days, 'e');
            }
            setTimerWhether(false);
        }
    }, [purpose, periodWhether]);

    const currDate = new Date();
    const onDateChange = async (date: Date | undefined, rangeType: 's' | 'e') => {
        if (!date) {
            setStartDate(undefined);
            setEndDate(undefined);
            return;
        }
        if (isCurrentlyLate(moment(date).format('YYYY.MM.DD HH:mm'))) {
            date = new Date(getDefaultEvalDate(moment().format('YYYY.MM.DD HH:mm')));
        }
        const start = date;
        const end = endDate ? endDate : undefined;
        switch (rangeType) {
            case 's':
                setStartDate(date);
                // 시작시간이 변경되었을 때, 종료시간이 시작시간보다 이전이면 종료시간을 null로 설정
                if (end && start.getTime() >= end.getTime()) {
                    setEndDate(undefined);
                }
                break;
            case 'e':
                setEndDate(date);
                break;
        }
        if (end && start.getTime() > end.getTime()) {
            setEndDate(start);
        }
    };

    const handlePeriodWhetherChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setPeriodWhether(e.target.checked);
        if (e.target.checked) setTimerWhether(false);
        let total = hours * 60 + minutes;
        if (purpose === 'class' && !e.target.checked) total = Math.min(total, MAX_CLASS_MIN);
        if (purpose === 'class' && e.target.checked) total = Math.min(total, MAX_CLASS_PERIOD_MIN);
        const newHours = Math.floor(total / 60);
        const newMinutes = total % 60;
        setHours(newHours);
        setMinutes(newMinutes);
    };

    return (
        <div className="set-file-box cont-mt step3">
            <div className="set-file-header">
                <div className="set-file-title">
                    <h3 className="title step_tab_title">STEP {purpose === 'class' ? 3 : 2}</h3>
                </div>
            </div>
            <div className="set-file-content content-type1">
                <>
                    <div className="form-row">
                        <div className="form-label size-type1 title_wrap">
                            <div className="option_title">
                                응시 기간
                                {purpose === 'URL' || (purpose === 'class' && periodWhether)
                                    ? ' *'
                                    : ''}
                            </div>
                        </div>
                        <div className="form-td">
                            <div className="cont_wrap">
                                <div className="flex_b cont_wrap_inner">
                                    <div className="form-period calenderBox">
                                        <div className="datepicker">
                                            <DatePicker
                                                disabled={!periodWhether && purpose === 'class'}
                                                placeholderText={'시작 일 선택'}
                                                selectsStart
                                                date={startDate!}
                                                maxDate={endDate}
                                                minDate={currDate}
                                                changed_date={(date: Date) =>
                                                    onDateChange(date, 's')
                                                }
                                            />
                                        </div>
                                        <div>~</div>
                                        <div className="datepicker">
                                            <DatePicker
                                                disabled={!periodWhether && purpose === 'class'}
                                                placeholderText={'종료 일 선택'}
                                                selectsEnd
                                                date={endDate!}
                                                minDate={startDate ? startDate : currDate}
                                                maxDate={new Date('9999-12-31')}
                                                changed_date={(date: Date) =>
                                                    onDateChange(date, 'e')
                                                }
                                            />
                                        </div>
                                    </div>
                                    {purpose === 'class' && (
                                        <div className="form-timer">
                                            <label
                                                className="base-toggle toggle-type1"
                                                htmlFor="toggle"
                                            >
                                                <input
                                                    type="checkbox"
                                                    id="toggle"
                                                    checked={periodWhether}
                                                    onChange={(e) => handlePeriodWhetherChange(e)}
                                                />
                                                <i>
                                                    <span className="toggle-state state-off">
                                                        OFF
                                                    </span>
                                                    <span className="toggle-state state-on">
                                                        ON
                                                    </span>
                                                </i>
                                            </label>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="form-row mt0">
                        <div className="form-label size-type1 title_wrap">
                            <div className="option_title"></div>
                        </div>
                        <div className="form-td">
                            <div className="cont_wrap">
                                <div className="cont_wrap_inner">
                                    <div className="info_wrap">
                                        <p className="txt-ex info2">
                                            {periodWhether || purpose !== 'class'
                                                ? '선생님이 응시 기간을 설정하면 학생들은 그 기간 안에 평가를 풀어 제출할 수 있습니다.'
                                                : '선생님이 평가를 시작해 학생들이 평가를 풀이하는 화면을 실시간으로 모니터링할 수 있습니다.'}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
                <>
                    <div className="form-row">
                        <div className="form-label size-type1 title_wrap">
                            <div className="option_title">
                                제한 시간
                                {timerWhether && ' *'}{' '}
                            </div>
                        </div>
                        <div className="form-td">
                            <div className="cont_wrap">
                                <div className="cont_wrap_inner flex_b">
                                    <div className="form-timer">
                                        <div className="timer-item">
                                            <div className="text-box">
                                                <input
                                                    type="text"
                                                    title="타이머 시 입력"
                                                    maxLength={2}
                                                    value={hours}
                                                    onChange={(e) =>
                                                        handleInputDateChange(
                                                            e,
                                                            'hours',
                                                            purpose === 'class' ? 1 : 2,
                                                            periodWhether ? 'Y' : 'N',
                                                            hours,
                                                            minutes,
                                                            setHours,
                                                            setMinutes
                                                        )
                                                    }
                                                    disabled={!timerWhether}
                                                />
                                            </div>
                                            <span className="timer-txt">시간</span>
                                        </div>
                                        <div className="timer-item">
                                            <div className="text-box">
                                                <input
                                                    type="text"
                                                    title="타이머 분 입력"
                                                    maxLength={2}
                                                    value={minutes}
                                                    onChange={(e) =>
                                                        handleInputDateChange(
                                                            e,
                                                            'minutes',
                                                            purpose === 'class' ? 1 : 2,
                                                            periodWhether ? 'Y' : 'N',
                                                            hours,
                                                            minutes,
                                                            setHours,
                                                            setMinutes
                                                        )
                                                    }
                                                    disabled={!timerWhether}
                                                />
                                            </div>
                                            <span className="timer-txt">분</span>
                                        </div>
                                        <div className="timer-item timerbtn">
                                            {timeAddArr.map((e, i) => (
                                                <button
                                                    key={`add-btn-${i}`}
                                                    type="button"
                                                    className="btn btn-icon2 plus_small"
                                                    onClick={() =>
                                                        addMinutes(
                                                            e.value,
                                                            purpose === 'class' ? 1 : 2,
                                                            periodWhether ? 'Y' : 'N',
                                                            hours,
                                                            minutes,
                                                            setHours,
                                                            setMinutes
                                                        )
                                                    }
                                                    disabled={!timerWhether}
                                                >
                                                    {e.label}
                                                </button>
                                            ))}
                                        </div>
                                        {(purpose !== 'class' ||
                                            (purpose === 'class' && periodWhether)) && (
                                            <label
                                                className="base-toggle toggle-type1"
                                                htmlFor="toggle2"
                                            >
                                                <input
                                                    type="checkbox"
                                                    id="toggle2"
                                                    checked={timerWhether}
                                                    onChange={(e) =>
                                                        setTimerWhether(e.target.checked)
                                                    }
                                                />
                                                <i>
                                                    <span className="toggle-state state-off">
                                                        OFF
                                                    </span>
                                                    <span className="toggle-state state-on">
                                                        ON
                                                    </span>
                                                </i>
                                            </label>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    {purpose === 'class' && (
                        <div className="form-row">
                            <div className="form-label size-type1 title_wrap">
                                <div className="option_title"></div>
                            </div>
                            <div className="form-td">
                                <div className="cont_wrap">
                                    <div className="cont_wrap_inner">
                                        <div className="info_wrap">
                                            <p className="txt-ex info2">
                                                응시 기간이 없는 평가는 최대 2시간 평가를 진행할 수
                                                있습니다.
                                            </p>
                                            <p className="txt-ex info2">
                                                평가 [종료하기]를 실행하여 설정된 시간 보다 먼저
                                                평가를 종료할 수 있습니다.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </>
            </div>
        </div>
    );
};
